import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type PlaygroundRequest, aiApi } from '../ai';

export const aiKeys = {
  settings: ['ai', 'settings'] as const,
  models: ['ai', 'models'] as const,
  usage: (month?: string) => ['ai', 'usage', month ?? 'current'] as const,
};

export function useAiSettingsQuery() {
  return useQuery({ queryKey: aiKeys.settings, queryFn: () => aiApi.getSettings() });
}

/** Only asked for once a key is stored and the switch is on; the route answers 412 otherwise. */
export function useAiModelsQuery(enabled: boolean) {
  return useQuery({ queryKey: aiKeys.models, queryFn: () => aiApi.getModels(), enabled, retry: false });
}

export function useAiUsageQuery(month?: string) {
  return useQuery({ queryKey: aiKeys.usage(month), queryFn: () => aiApi.getUsage(month) });
}

function useInvalidateAiQueries() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: aiKeys.settings });
    qc.invalidateQueries({ queryKey: aiKeys.models });
    qc.invalidateQueries({ queryKey: ['ai', 'usage'] });
  };
}

export function useSetAiKeyMutation() {
  const invalidate = useInvalidateAiQueries();
  return useMutation({ mutationFn: (apiKey: string) => aiApi.setKey(apiKey), onSuccess: invalidate });
}

export function useClearAiKeyMutation() {
  const invalidate = useInvalidateAiQueries();
  return useMutation({ mutationFn: () => aiApi.clearKey(), onSuccess: invalidate });
}

export function useSetAiEnabledMutation() {
  const invalidate = useInvalidateAiQueries();
  return useMutation({ mutationFn: (enabled: boolean) => aiApi.setEnabled(enabled), onSuccess: invalidate });
}

export function useSetAiDefaultsMutation() {
  const invalidate = useInvalidateAiQueries();
  return useMutation({
    mutationFn: (payload: { defaultModel: string; monthlyBudgetUsd: number }) => aiApi.setDefaults(payload),
    onSuccess: invalidate,
  });
}

/** A run spends money and changes the month's totals, so the settings view is refreshed after it. */
export function useAiPlaygroundMutation() {
  const invalidate = useInvalidateAiQueries();
  return useMutation({ mutationFn: (payload: PlaygroundRequest) => aiApi.playground(payload), onSuccess: invalidate });
}
